import { createRoute, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { RootRoute } from "../root-route";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { StatusCard } from "@/components/status-card";
import { getKommunStatusByKod, getNoticesForKommun, type NoticeEvent } from "@/lib/api";
import { RESTRIKTION_LABEL, RESTRIKTION_CLASSES, formatDate, notisEffectiveDatum, notisEffectiveInFuture } from "@/lib/status";
import { MapPin, ArrowLeft, CalendarDays, ExternalLink } from "lucide-react";
import { Link } from "@tanstack/react-router";

function notisLabel(n: NoticeEvent): string {
  const future = notisEffectiveInFuture(n.notis_typ, n.datum_fran, n.datum_till);
  if (n.notis_typ === "upphavning") return future ? "Restriktion upphör" : "Restriktion upphävd";
  if (n.notis_typ === "introduktion") return future ? "Restriktion införs" : "Restriktion införd";
  return "Uppdatering";
}

function breadcrumbJsonLd(kommunNamn: string, kommunkod: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Bevattningskartan",
        item: "https://bevattningskartan.se/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: `${kommunNamn} - bevattningsförbud`,
        item: `https://bevattningskartan.se/kommun/${kommunkod}`,
      },
    ],
  };
}

export const KommunRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/kommun/$kommunkod",
  component: KommunPage,
  head: ({ params }) => ({
    meta: [
      { title: `Bevattningsförbud ${params.kommunkod} | Bevattningskartan` },
      { name: "description", content: `Aktuell status för bevattningsförbud i kommun ${params.kommunkod}. Se senaste beslut, period och källa hos Bevattningskartan.` },
      { name: "robots", content: "index, follow" },
    ],
    links: [
      { rel: "canonical", href: `https://bevattningskartan.se/kommun/${params.kommunkod}` },
      { rel: "alternate", hrefLang: "sv", href: `https://bevattningskartan.se/kommun/${params.kommunkod}` },
      { rel: "alternate", hrefLang: "x-default", href: `https://bevattningskartan.se/kommun/${params.kommunkod}` },
    ],
  }),
});

function KommunPage() {
  const { kommunkod } = useParams({ from: "/kommun/$kommunkod" });

  const { data: rows = [], isLoading: statusLoading } = useQuery({
    queryKey: ["kommun-status", kommunkod],
    queryFn: () => getKommunStatusByKod(kommunkod),
  });

  const { data: notices = [] } = useQuery({
    queryKey: ["notices", kommunkod],
    queryFn: () => getNoticesForKommun(kommunkod),
  });

  const kommunNamn = rows[0]?.kommun_namn ?? `Kommun ${kommunkod}`;
  const lan = rows[0]?.lan ?? null;
  const hasData = rows.length > 0;

  const pageTitle = `Bevattningsförbud ${kommunNamn} ${new Date().getFullYear()} | Aktuell status`;
  const pageDescription = `Se aktuell status för bevattningsförbud i ${kommunNamn}. Uppdateras löpande från kommunens VA-huvudman.`;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <nav aria-label="Brödsmula">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <li>
              <Link to="/" className="hover:text-foreground">
                Startsida
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground">{kommunNamn}</li>
          </ol>
        </nav>

        <header className="mt-8 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <MapPin className="h-3.5 w-3.5" /> {lan ?? "Sverige"}
          </span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl text-foreground">
            Bevattningsförbud {kommunNamn}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Aktuell status och historik för bevattningsförbud i {kommunNamn}. Informationen hämtas automatiskt från kommunens VA-huvudman.
          </p>
        </header>

        <section className="mt-10">
          {statusLoading ? (
            <div className="h-40 w-full animate-pulse rounded-2xl bg-muted/40" />
          ) : hasData ? (
            <StatusCard kommunNamn={kommunNamn} data={rows} />
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-8 text-center text-muted-foreground">
              Ingen data tillgänglig för {kommunNamn} just nu. Vi gissar aldrig — kontrollera alltid med din VA-huvudman.
            </div>
          )}
        </section>

        {notices.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl text-foreground">Senaste händelser</h2>
            <div className="mt-4 space-y-3">
              {notices.map((notice) => (
                <div key={notice.id} className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${RESTRIKTION_CLASSES[notice.notis_typ === 'upphavning' ? 'ingen' : 'rekommendation'].dot}`} />
                    <span className="text-sm font-semibold text-foreground">{notisLabel(notice)}</span>
                    {(() => {
                      const eff = notisEffectiveDatum(notice.notis_typ, notice.datum_fran, notice.datum_till);
                      return eff ? (
                        <span className="text-xs text-muted-foreground">
                          · {notice.notis_typ === "upphavning" ? "upphör" : "gäller från"} {formatDate(eff)}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {notice.omraden_text ?? notice.regler_text ?? "Ingen ytterligare information tillgänglig."}
                  </p>
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CalendarDays className="h-3.5 w-3.5" />
                    <span>{formatDate(notice.datum_publicerad)}</span>
                    {notice.url && (
                      <a href={notice.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                        Källa <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till kartan
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
